<template>
  <layout-navigation>
    <scrollable-container :class="$style.mainContainer">
      <section :class="$style.page">
        <div :class="$style.header">
          <div :class="$style.control">
            <div
              v-if="isDateSelectRequired"
              :class="$style.dateSelect"
            >
              <vue-select
                v-model="selectedOption"
                :options="options"
                :reduce="(option) => option.id"
                label="name"
                :searchable="false"
                :clearable="false"
                @input="setDateOption()"
              />
            </div>

            <div
              v-if="isTreeSelectComputerRequired"
              :class="$style.treeSelectContainer"
            >
              <tree-select-computers :disabled-folder="true" />
            </div>

            <store-based-date-time-picker
              :clearable="false"
              :disabled="isDataPickerDisabled"
              :class="[{ [$style.columnDatePicker]: isHeatMap }]"
            />

            <div
              v-if="isPrinterSelectRequired"
              :class="$style.vueSelectContainer"
            >
              <vue-select-custom
                name="Printer"
                :top-placeholder="$t('reports.searchAndSelectPrinter')"
                :bottom-placeholder="$t('reports.enterName')"
                :options="$store.state.filters.selects.printer.options"
              />
            </div>

            <div
              v-if="isComputerSelectRequired"
              :class="$style.vueSelectContainer"
            >
              <vue-select-custom
                name="Computer"
                :top-placeholder="$t('reports.searchAndSelectComputer')"
                :bottom-placeholder="$t('reports.enterName')"
                :options="$store.state.filters.selects.computer.options"
              />
            </div>

            <div
              v-if="isTableGroupCheckboxRequired"
              :class="$style.tableGroupCheckboxContainer"
            >
              <table-group-checkbox />
            </div>

            <div
              v-if="isWorktimeOnlyCheckboxRequired"
              :class="$style.worktimeOnlyCheckboxContainer"
            >
              <worktime-only-checkbox />
            </div>

            <div
              v-if="isSwitcherRequired"
              :class="$style.switcher"
            >
              <button
                :class="[$style.switchButton, { [$style.activeSwitchButton]: switcher === 1 }]"
                @click="switchValue(1)"
              >
                {{ $t('global.time') }}
              </button>
              <button
                :class="[$style.switchButton, { [$style.activeSwitchButton]: switcher === 2 }]"
                @click="switchValue(2)"
              >
                {{ $t('reports.arrivalDeparture') }}
              </button>
            </div>

            <div
              v-if="isTreeSelectRequired"
              :class="$style.treeSelectContainer"
            >
              <tree-select />
            </div>
          </div>
          <div :class="$style.control" />
          <div :class="[$style.control, $style.buttonsControl]">
            <button
              :class="$style.printButton"
              :disabled="!isTableReady"
              @click="print"
            >
              {{ $t('global.print') }}
            </button>

            <div :class="[$style.dropdown, $style.buttonContainer]">
              <button
                :class="[$style.printButton, $style.exportButton]"
                :disabled="!isTableReady"
              >
                {{ $t('global.exportTo') }}
              </button>
              <div
                v-if="isExportRequired && isTableReady"
                :class="$style.dropdownСontent"
              >
                <div
                  v-for="option in exportOptions"
                  :key="option"
                  :class="$style.dropdownItem"
                  :v-bind:value="option"
                  @click="exportTable(option)"
                >
                  {{ option }}
                </div>
              </div>
              <div
                v-else-if="isTableReady"
                :class="$style.dropdownСontent"
              >
                <div
                  :class="$style.dropdownItem"
                  @click="exportTable(typePdf)"
                >
                  pdf
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <router-view />
    </scrollable-container>
    <container
      v-if="isMultiSelectRequired"
      :class="[$style.multiSelect, { [$style.hidden]: !isOpened }]"
    >
      <multi-select
        :is-opened="isOpened"
        @toggle="toggle"
      />
    </container>
  </layout-navigation>
</template>

<script>
  import Vue from 'vue';

  import StoreBasedDateTimePicker from '@/components/common/filter/store-based-date-time-picker.vue';
  import LayoutNavigation from '@/layouts/navigation.vue';
  import ScrollableContainer from '@/components/common/blocks/scrollable-container.vue';
  import VueSelectCustom from '@/components/common/header/vue-select-custom/select.vue';
  import MultiSelect from '@/components/common/filter/multi-select.vue';
  import TableGroupCheckbox from '@/components/common/header/table-group-checkbox.vue';
  import WorktimeOnlyCheckbox from '@/components/common/header/worktime-only-checkbox.vue';
  import Container from '@/components/common/blocks/container.vue';
  import TreeSelect from '@/components/common/header/tree-select.vue';
  import TreeSelectComputers from '@/components/common/header/tree-select-computers.vue';
  import html2pdf from 'html-to-pdf-js';

  import exportFromJson from 'export-from-json';
  import * as dayjs from 'dayjs';
  import VueSelect from 'vue-select';
  import { getDataField } from '@/views/reports/users/worktime/table-field';
  import { getTime } from '@/helpers/format-date';
  import {
    ACTIVITY_FEED,
    DATE_OPTIONS,
    FORMAT_EXPORT,
    EXPORT_OPTIONS,
    ROUTE_FILES,
    ROUTE_HEAT_MAP,
    ROUTE_NAMES_AUTOTABLE,
    ROUTE_NAMES_SERVER_EXPORT_PDF,
    ROUTE_TRAFFIC_BY_GROUPS,
    ROUTE_TRAFFIC_DETAILED,
    TRAFFIC_KIND_OPTIONS,
  } from '@/views/reports/const/const';

  import '@/components/common/logs/mixins/Roboto-Regular-normal';
  import JsPdf from 'jspdf';
  import autoTable from 'jspdf-autotable';
  import VueToast from 'vue-toast-notification';
  import 'vue-toast-notification/dist/theme-default.css';
  import { REPORT_ENUM } from '@/assets/scripts/reportEnum';
  import { getPdf } from '@/api/methods/pdf';

  Vue.use(VueToast);

  export default {
    components: {
      LayoutNavigation,
      ScrollableContainer,
      VueSelectCustom,
      StoreBasedDateTimePicker,
      MultiSelect,
      TableGroupCheckbox,
      WorktimeOnlyCheckbox,
      Container,
      TreeSelect,
      VueSelect,
      TreeSelectComputers,
    },
    data: () => ({
      isOpened: true,
      isAllFiles: false,
      switcher: 1,
      selectedOption: 3,
      tableData: {},
      computersArray: [],
    }),
    computed: {
      options() {
        if (this.isTrafficRoute) return TRAFFIC_KIND_OPTIONS;
        return DATE_OPTIONS;
      },
      isTrafficRoute() {
        return ROUTE_TRAFFIC_BY_GROUPS === this.$route.name || ROUTE_TRAFFIC_DETAILED === this.$route.name;
      },
      isHeatMap() {
        return ROUTE_HEAT_MAP === this.$route.name;
      },
      isTableReady() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.TABLE_READY;
      },
      dateRange() {
        return this.$store.getters['filters/formattedDateRange'];
      },
      isFiles() {
        return ROUTE_FILES === this.$route.name;
      },
      typePdf() {
        return FORMAT_EXPORT.PDF;
      },
      exportOptions() {
        return EXPORT_OPTIONS;
      },
      printData() {
        return this.$store.state.logs.printData.data;
      },
      printColumns() {
        return this.$store.state.logs.printData.columns;
      },
      isDateSelectRequired() {
        return this.$route.meta.requiredStoreParameters.dateSelect === true;
      },
      isDataPickerDisabled() {
        if (!this.isTrafficRoute && this.isDateSelectRequired && this.selectedOption !== 3) return true;
        return false;
      },
      isPrinterSelectRequired() {
        return this.$route.meta.requiredStoreParameters.printer === true;
      },
      isComputerSelectRequired() {
        return this.$route.meta.requiredStoreParameters.computer === true;
      },
      isMultiSelectRequired() {
        return this.$route.meta.requiredStoreParameters.multiSelect === true;
      },
      isTableGroupCheckboxRequired() {
        return this.$route.meta.requiredStoreParameters.tableGroup === true;
      },
      isWorktimeOnlyCheckboxRequired() {
        return this.$route.meta.requiredStoreParameters.worktimeOnly === true;
      },
      isSwitcherRequired() {
        return this.$route.meta.requiredStoreParameters.switcher === true;
      },
      isTreeSelectRequired() {
        return this.$route.meta.requiredStoreParameters.treeSelect === true;
      },
      isTreeSelectComputerRequired() {
        return this.$route.meta.requiredStoreParameters.selectedComputer === true;
      },
      isExportRequired() {
        return this.$route.meta.requiredStoreParameters.export === true;
      },
    },
    watch: {
      isTrafficRoute: {
        async handler(newVal) {
          if (newVal) this.selectedOption = 0;
          else this.selectedOption = 3;

          this.setDateOption();
        },
        immediate: true,
      },
    },
    methods: {
      getDataField(data, field) {
        return getDataField(data, field);
      },
      getTime(data) {
        return getTime(data);
      },
      setDateOption() {
        if (this.isTrafficRoute) {
          this.$store.commit('filters/setTrafficSelect', this.selectedOption);
          return;
        }

        this.$store.commit('filters/setDateSelect', this.selectedOption);
      },
      switchValue(value) {
        this.switcher = value;
        this.$store.commit('filters/setSwitcher', value);
      },
      toggle() {
        this.isOpened = !this.isOpened;
      },
      rftcharcode(c) {
        const cc = c.charCodeAt();
        return cc <= 32767 ? cc : cc - 65535;
      },

      async pdfExportFromHtml(data, filename) {
        const { name } = this.$route;

        const target = document.getElementById('printable');
        const copy = target.cloneNode(true);

        const originalCanvas = target.querySelectorAll('canvas');
        const copiedCanvas = copy.querySelectorAll('canvas');

        copiedCanvas.forEach((canvas, i) => {
          const original = originalCanvas[i];

          const img = new Image();
          img.src = original.toDataURL('image/png');

          img.style.objectFit = 'contain';
          img.style.display = 'block';

          canvas.replaceWith(img);
        });

        const dateFrom = dayjs(this.dateRange.dateFrom).format('DD.MM.YY');
        const dateTo = dayjs(this.dateRange.dateTo).format('DD.MM.YY');

        const wrapper = document.createElement('div');

        wrapper.style.width = '100%';
        wrapper.style.display = 'block';

        const title = document.createElement('div');

        title.style.cssText = `
          margin:5px 5px 15px 5px;
          display:flex;
          justify-content:center;
        `;

        title.textContent = `${data?.name} c: ${dateFrom} по: ${dateTo}`;

        copy.style.marginTop = '0';

        copy.prepend(title);
        wrapper.appendChild(copy);

        let orientation = 'portrait';
        const thead = copy.getElementsByTagName('thead');
        if (thead.length > 0) {
          const tdArray = thead[0].children[0].children;

          if (tdArray && tdArray.length > 8) orientation = 'landscape';
        }

        const opt = {
          margin: 10,
          filename: `${filename}.pdf`,
          pagebreak: { mode: ['css'] },
          // pagebreak: { mode: ['avoid-all', 'css'] },
          jsPDF: { orientation },
          html2canvas: { scale: 4 },
        };

        if (name === 'worktimeDetails' || name === 'productivity') {
          opt.html2canvas.scale = 3;

          let pages = [];

          const html = wrapper.innerHTML;

          if (name === 'worktimeDetails') {
            pages = html.split(/(?=<div class="worktime-report_userStickyToggle)/);
          } else if (name === 'productivity') {
            pages = html.split(/(?=<div class="productivity-report_userStickyToggle)/);
          }

          if (pages[1]) {
            pages[1] = pages[0] + pages[1];
            pages.shift();
          }

          let worker = html2pdf().set(opt).from(pages[0]).toPdf();
          pages.slice(1).forEach((page) => {
            worker = worker
              .get('pdf')
              .then((pdf) => {
                pdf.addPage();
              })
              .from(page)
              .toContainer()
              .toCanvas()
              .toPdf();
          });
          await worker.save();
        } else await html2pdf().set(opt).from(wrapper).save();
      },

      async getPieChare(doc, index) {
        const widthImg = 80;
        const heightImg = 100;
        const heightPage = 297;
        const axisX = 15;
        let axisY = 10;
        const pieChartData = document.getElementById(`pieChartData_${index}`);
        const imgPieChartData = pieChartData.toDataURL('image/jpeg', 1.0);
        const pieChartAttitude = document.getElementById(`pieChartAttitude_${index}`);
        const imgPieChartAttitude = pieChartAttitude.toDataURL('image/jpeg', 1.0);
        axisY += doc.lastAutoTable.finalY;
        const diff = heightPage - axisY;
        if (diff < heightImg) {
          axisY = 15;
          doc.addPage();
        }
        doc.addImage(imgPieChartData, 'JPEG', axisX, axisY, widthImg, heightImg);
        doc.addImage(imgPieChartAttitude, 'JPEG', axisX + widthImg + 10, axisY, widthImg, heightImg);
        return doc;
      },

      async generatePdf(doc, options, logname, filename, indexTable) {
        doc.addFont('Roboto-Regular-normal.ttf', 'Roboto-Regular', 'normal');
        doc.setFont('Roboto-Regular');
        doc.setFontSize(12);

        const dateFrom = dayjs(this.dateRange.dateFrom).format('DD.MM.YY');
        const dateTo = dayjs(this.dateRange.dateTo).format('DD.MM.YY');

        doc.text(`${logname} c: ${dateFrom} по: ${dateTo}`, 15, 10);

        autoTable(doc, {
          ...options,
        });

        if (this.isFiles) {
          doc = await this.getPieChare(doc, indexTable);
          doc.addPage();
          return doc;
        }
        doc.save(`${filename} ${dateFrom}-${dateTo}.pdf`);

        return doc;
      },

      async getPdfTableActivity(logname, filename) {
        const doc = new JsPdf('landscape');
        const head = this.printColumns.map((item) => item.name);
        const body = this.printData.map((item) => {
          const rowData = [];
          this.printColumns.forEach(({ field }) => {
            let result = this.getDataField(item[field], field);
            if (field === 'eventTime') {
              result += this.getTime(item[field]);
            }
            rowData.push(result);
          });
          return rowData;
        });

        const willDrawCell = (data) => {
          const { row } = data;
          if (data.section === 'body') {
            if (row.raw[6] === 'Продуктивно') {
              doc.setFillColor(124, 216, 144);
            }
            if (row.raw[6] === 'Непродуктивно') {
              doc.setFillColor(255, 176, 176);
            }
            if (row.raw[6] === 'Нейтрально') {
              doc.setFillColor(255, 236, 188);
            }
          }
        };

        const options = {
          head: [head],
          body,
          willDrawCell,
          showHead: 'firstPage',
          startY: 15,
          styles: {
            font: 'Roboto-Regular',
            fontStyle: 'normal',
            minCellWidth: '30',
          },
          alternateRowStyles: { fillColor: [255, 255, 255] },
          columnStyles: {
            6: { cellWidth: 40 },
          },
        };
        await this.generatePdf(doc, options, logname, filename);
      },
      async getPdfTableFiles(logname, filename, doc, indexTable) {
        const head = this.printColumns.map(({ name }) => name);
        const body = this.printData.map((item) => {
          const rowData = [];
          this.printColumns.forEach(({ field }) => rowData.push(item[field]));
          return rowData;
        });

        const options = {
          head: [head],
          body,
          theme: 'grid',
          showHead: 'firstPage',
          startY: 15,
          styles: {
            font: 'Roboto-Regular',
            fontStyle: 'normal',
            minCellWidth: '30',
          },
          columnWidth: 'wrap',
        };

        return this.generatePdf(doc, options, logname, filename, indexTable);
      },

      async allFilesPdfExport(data, logname, filename, doc) {
        try {
          let pdf;
          let promise = Promise.resolve();

          data.forEach((table, index) => {
            promise = promise.then(async () => {
              this.$store.commit('logs/printData/setColumns', table.columns);
              this.$store.commit('logs/printData/setData', table.data);
              pdf = await this.getPdfTableFiles(logname, filename, doc, index);
            });
          });

          promise.then(() => {
            const dateFrom = dayjs(this.dateRange.dateFrom).format('DD.MM.YY');
            const dateTo = dayjs(this.dateRange.dateTo).format('DD.MM.YY');

            const pageCount = pdf.internal.getNumberOfPages();

            pdf.deletePage(pageCount);
            pdf.save(`${logname} ${dateFrom}-${dateTo}.pdf`);
          });

          Vue.$toast.open({
            message: 'Документ pdf готов',
            type: 'success',
          });
        } catch (error) {
          console.error('Что-то пошло не так...', error);
          Vue.$toast.open({
            message: 'Что-то пошло не так...',
            type: 'error',
          });
        }
      },

      async pdfExport(data, logname, filename) {
        if (this.$route.name === ACTIVITY_FEED) {
          await this.getPdfTableActivity(logname, filename);
        } else {
          const doc = new JsPdf('portrait');

          await this.allFilesPdfExport(data, logname, filename, doc);
        }
      },
      async getExportFromServer(exportData, filename) {
        const selectedComputers = this.$store.getters['pageSpecificData/checkedComputers'];

        const url = new URL(window.location.href);
        url.searchParams.set('pdf', 'true');
        const dateFrom = dayjs(this.dateRange.dateFrom).format('DD.MM.YY');
        const dateTo = dayjs(this.dateRange.dateTo).format('DD.MM.YY');

        const title = `${exportData.name} ${this.$t('export.fromTo', {
          from: dateFrom,
          to: dateTo,
        })}`;
        await getPdf(url, filename, title, selectedComputers);
      },
      async exportTable(type) {
        const { query, name } = this.$route;
        let filename = `${name} - ${query.dateFrom} - ${query.dateTo}`;
        const exportData = this.$store.getters[`exportData/${name}`];

        const isAutotable = ROUTE_NAMES_AUTOTABLE.includes(name);
        const isServerExport = ROUTE_NAMES_SERVER_EXPORT_PDF.includes(name);
        const isPdf = type === FORMAT_EXPORT.PDF;
        this.$store.commit('loader/setIsLoaderNeeded', true);

        if (isPdf && isServerExport) {
          await this.getExportFromServer(exportData, filename);
        } else if (isPdf && !isAutotable) {
          await this.pdfExportFromHtml(exportData, filename);
        } else if (isPdf && isAutotable) {
          if (!exportData?.data?.length) {
            this.$store.commit('loader/setIsLoaderNeeded', false);

            Vue.$toast.open({
              message: 'Нет данных',
              type: 'info',
            });
            return;
          }
          await this.pdfExport(exportData.data, exportData.name, filename);
        } else {
          /* if (type === 'rtf') {
           const target = document.getElementById('printable');

           filename += '.rtf';
           const html = target.outerHTML;
           const rtfContent = htmlToRtf.convertHtmlToRtf(html);
           const self = this;

           // eslint-disable-next-line
           const content = rtfContent.replace(/[^\x00-\x7F]/g, (c) => `\\u${self.rftcharcode(c)}?`);
           const type = 'application/rtf'

           const file = new Blob([content], { type });
           const downloadLink = document.createElement('a');
           downloadLink.download = filename;
           downloadLink.href = window.URL.createObjectURL(file);
           downloadLink.style.display = 'none';
           document.body.appendChild(downloadLink);
           downloadLink.click();
         }
         else { */
          filename += '.xls';

          exportFromJson({
            data: exportData.data,
            fileName: filename,
            exportType: type,
          });
        }
        this.$store.commit('loader/setIsLoaderNeeded', false);
      },

      print() {
        const prtHtml = document.getElementById('printable').innerHTML;

        let stylesHtml = '';
        [...document.querySelectorAll('link[rel="stylesheet"], style')].forEach((node) => {
          stylesHtml += node.outerHTML;
        });
        stylesHtml += '<style type="text/css">html{width:21cm;height:29.7cm;}body{display:block!important;}</style>';

        const WinPrint = window.open('', '', 'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=yes,status=yes');

        WinPrint.document.write(`<!DOCTYPE html>
                              <html>
                                <head>
                                  ${stylesHtml}
                                </head>
                                <body>
                                  ${prtHtml}
                                </body>
                              </html>`);

        WinPrint.document.close();
        WinPrint.focus();
        WinPrint.print();
        WinPrint.close();
      },
    },
  };
</script>

<style lang="scss" module>
  .titlePDf {
    padding: 18px;
    text-align: center;
    font-weight: 500;
  }
  .mainContainer {
    background: $white;
    box-shadow: $shadow;
    flex-grow: 1;
  }

  .header {
    box-shadow: $shadow;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px;
    margin-bottom: 19px;
  }

  .control {
    display: flex;
    align-items: flex-end;
    margin: 0px 5px;
  }

  .buttonsControl {
    @media (max-width: 1400px) {
      flex-direction: column;
      gap: 5px;
    }
  }

  .vueSelectContainer {
    width: 270px;
    height: 54px;
    margin-left: 20px;
  }

  .multiSelect {
    min-width: 250px;
    margin-left: 20px;
    background-color: $white;
    padding: 20px;
    overflow: hidden;
  }

  .hidden {
    min-width: 62px;
  }

  .tableGroupCheckboxContainer {
    width: 132px;
    height: 54px;
    margin-left: 20px;
    border: 1px solid #dfdfdf;
    border-radius: 0px;
  }

  .worktimeOnlyCheckboxContainer {
    width: 152px;
    height: 54px;
    margin: 0px 7px 0px 7px;
    border: 1px solid #dfdfdf;
    border-radius: 0px;
  }

  .printButton {
    cursor: pointer;
    display: block;

    width: 95px;
    height: 28px;
    float: right;

    background: $blue;
    border: 2px solid $blue;
    border-radius: 2px;

    font: inherit;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    text-align: center;
    color: $white;

    margin: 0px 5px;
    &:disabled {
      cursor: not-allowed;
      background: $branchColor;
      border: 2px solid $branchColor;
    }
  }

  .treeSelectContainer {
    margin: 0px 7px;
    width: 45%;
    min-width: 170px;
  }

  .exportButton {
    background: white;
    color: #2985bf;
    &:disabled {
      cursor: not-allowed;
      color: $branchColor;
      border: 2px solid $branchColor;
      background: white;
    }
  }

  .dropdown {
    position: relative;
  }

  .dropdownСontent {
    display: none;
    position: absolute;
    background-color: white;
    box-shadow: 2px 3px 5px rgba(0, 0, 0, 0.2);
    z-index: 50;
    top: 28px;
    width: 95px;
    font-size: 14px;
    left: 5px;
  }

  .dropdownItem {
    color: black;
    padding: 12px 16px;
    text-decoration: none;
    display: block;
    cursor: pointer;
  }

  .dropdownСontent .dropdownItem:hover {
    background-color: #eee;
  }

  .dropdown:hover .dropdownСontent {
    display: block;
  }

  .switchButton {
    height: 48px;
    cursor: pointer;
    width: 120px;
    background: #bfcbd9;
    border: 0px;
    font: inherit;
    font-size: 14px;
    text-align: center;
    color: #ffffff;

    &:hover {
      background: $dark-gray;
      transition: background-color 0.5s linear;
    }

    &:disabled {
      background: $branchColor;
    }
  }

  .activeSwitchButton {
    background: #2985bf;
  }

  .switcher {
    margin: 20px;
  }

  .dateSelect {
    width: 200px;
    margin-right: 8px;
  }

  .columnDatePicker {
    flex-direction: column;
  }
</style>

<style>
  .v-select .vs__selected-options {
    padding: 6px 0 0 9px;
  }
  .v-select,
  .v-select ul {
    width: 100%;
  }
</style>
